import debounce from 'debounce-promise'
import { useContext, useEffect, useMemo, useRef } from 'react'

import { useDatesOrGuestsChanged } from './useDatesOrGuestsChanged'
import { getHotelUpdateSuccessSnackbarMessage } from './useSyncHotelTripItem'
import { useUpdateSavedItemBookmarkHandlers } from './useUpdateSavedItemSnackbarHandlers'

import { getDatesAndGuestsFromOfferPageState } from 'components/OfferPage/utils'
import GeoContext from 'contexts/geoContext'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import useBedbankRates from 'hooks/useBedbankRates'
import { getBedbankPriceAndValue } from 'lib/hotels/bedbankUtils'
import { useTrip, useUpdateTripItem } from 'tripPlanner/hooks/api'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import {
  AccommodationItem,
  BookmarkDatesAndOccupancy,
  HotelItem,
  LETripItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { isLeBookmark } from 'tripPlanner/utils'
import { createBedbankItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/bedbank'

export const useSyncBedbankTripItem = (
  state: OfferPageState,
  offer: App.BedbankOffer,
) => {
  const awaitingFilters = useRef(false)
  const attemptUpdates = useRef(false)
  const currentTripId = useAppSelector(getImmersiveTripId)
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { data: currentTrip } = useTrip({ tripId: currentTripId })
  const { showUpdateErrorSnackbar, showUpdateSuccessSnackbar } =
    useUpdateSavedItemBookmarkHandlers()
  const hotelItemRef = useRef<Array<HotelItem | AccommodationItem>>()

  hotelItemRef.current = currentTrip?.items.filter(
    (item: TripItem): item is HotelItem | AccommodationItem =>
      isLeBookmark(item) &&
      (item.type === 'HOTEL' || item.type === 'ACCOMMODATION') &&
      !item.isBooked &&
      item.code === offer.id,
  )

  const { mutate: updateMutate } = useUpdateTripItem({
    onSuccess: (item) => {
      // If the hotelItemRef exists, then we have a bookmarked item
      const savedHotelItem = item as Extract<
        LETripItem,
        HotelItem | AccommodationItem
      >
      showUpdateSuccessSnackbar(
        getHotelUpdateSuccessSnackbarMessage(savedHotelItem.savedItemData),
        item.tripId,
        item.id,
      )
    },
    onError: showUpdateErrorSnackbar,
  })

  const datesAndGuests = getDatesAndGuestsFromOfferPageState(state)
  const datesOrGuestsChanged = useDatesOrGuestsChanged(datesAndGuests)
  const filtersApplied = !state.activeFilter
  const hotelOnlyRatesRef = useRef<Array<App.BedbankRate> | undefined>(
    undefined,
  )
  const [{ hotelOnlyRates }] = useBedbankRates(
    offer.id,
    datesAndGuests.occupancies,
    datesAndGuests.checkInDate,
    datesAndGuests.checkOutDate,
  )
  hotelOnlyRatesRef.current = hotelOnlyRates

  const debouncedUpdate = useMemo(
    () =>
      debounce((datesAndGuests: BookmarkDatesAndOccupancy) => {
        const items = hotelItemRef.current
        const hotelOnlyRates = hotelOnlyRatesRef.current
        if (!items || !hotelOnlyRates || hotelOnlyRates.length === 0) return

        attemptUpdates.current = false
        items.forEach((item) => {
          const { price, value } = getBedbankPriceAndValue(
            hotelOnlyRates,
            item.subCode,
          )
          if (!price) return

          const payload = createBedbankItemPayload({
            offer,
            datesAndGuests,
            currentRegionCode,
            currencyCode: currentCurrency,
            price,
            value,
          })
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: payload,
          })
        })
      }, 2000),
    [offer, currentRegionCode, currentCurrency, updateMutate],
  )

  useEffect(() => {
    if (datesOrGuestsChanged) {
      awaitingFilters.current = true
    }
    if (awaitingFilters.current && filtersApplied) {
      awaitingFilters.current = false
      attemptUpdates.current = true
    }

    if (attemptUpdates.current) {
      debouncedUpdate(datesAndGuests)
    }
  })
}
